import { Shot } from '../../../types';
import { tosAccelerate } from '../../../utils/tosAccelerate';

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface EpisodePreviewPlayerProps {
  previewableShots: Shot[];
  previewCurrentIndex: number;
  activeBuffer: 'A' | 'B';
  initialReady: boolean;
  switchLoading: boolean;
  videoRefA: React.RefObject<HTMLVideoElement>;
  videoRefB: React.RefObject<HTMLVideoElement>;
  thumbnailStripRef: React.RefObject<HTMLDivElement>;
  t: Translate;
  getSelectedVideoURL: (shotId: string) => string;
  onPreviewEnded: () => void;
  onPreviewPlayingChange: (playing: boolean) => void;
  onPreviewCurrentIndexChange: (index: number) => void;
  onPlay: () => void;
}

export default function EpisodePreviewPlayer({
  previewableShots,
  previewCurrentIndex,
  activeBuffer,
  initialReady,
  switchLoading,
  videoRefA,
  videoRefB,
  thumbnailStripRef,
  t,
  getSelectedVideoURL,
  onPreviewEnded,
  onPreviewPlayingChange,
  onPreviewCurrentIndexChange,
  onPlay,
}: EpisodePreviewPlayerProps) {
  return (
    <>
      <div style={{ position: 'relative', width: '100%', maxHeight: '70vh', borderRadius: 'var(--radius)', overflow: 'hidden', background: '#000' }}>
        <video
          ref={videoRefA}
          controls
          onEnded={onPreviewEnded}
          onPlay={onPlay}
          onPause={() => onPreviewPlayingChange(false)}
          style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', display: 'block', position: activeBuffer === 'A' ? 'relative' : 'absolute', top: 0, left: 0, zIndex: activeBuffer === 'A' ? 2 : 1, opacity: activeBuffer === 'A' ? 1 : 0, pointerEvents: activeBuffer === 'A' ? 'auto' : 'none' }}
        />
        <video
          ref={videoRefB}
          controls
          onEnded={onPreviewEnded}
          onPlay={onPlay}
          onPause={() => onPreviewPlayingChange(false)}
          style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', display: 'block', position: activeBuffer === 'B' ? 'relative' : 'absolute', top: 0, left: 0, zIndex: activeBuffer === 'B' ? 2 : 1, opacity: activeBuffer === 'B' ? 1 : 0, pointerEvents: activeBuffer === 'B' ? 'auto' : 'none' }}
        />
        {(!initialReady || switchLoading) && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', gap: '0.75rem' }}>
            <div className="loading-spinner" style={{ width: '32px', height: '32px', borderWidth: '3px', borderColor: '#fff', borderTopColor: 'transparent' }}></div>
            {!initialReady && <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>{t('preview.loadingVideo')}</span>}
          </div>
        )}
      </div>

      <div ref={thumbnailStripRef} style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: '0.25rem' }}>
        {previewableShots.map((shot, index) => (
          <div
            key={shot.id}
            onClick={() => onPreviewCurrentIndexChange(index)}
            style={{
              flexShrink: 0,
              width: '100px',
              border: index === previewCurrentIndex ? '2px solid var(--primary)' : '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              overflow: 'hidden',
              cursor: 'pointer',
              background: index === previewCurrentIndex ? 'var(--primary-light)' : 'var(--surface)',
              transition: 'all var(--transition-fast)',
            }}
          >
            <video
              src={tosAccelerate(getSelectedVideoURL(shot.id))}
              muted
              preload="metadata"
              style={{ width: '100%', height: '56px', objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
            />
            <div style={{ padding: '2px 6px', fontSize: '0.75rem', textAlign: 'center', fontWeight: index === previewCurrentIndex ? 600 : 400, color: index === previewCurrentIndex ? 'var(--primary)' : 'var(--text-2)' }}>
              #{shot.index} · {shot.duration}s
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
